import { create } from 'zustand';
import {
  DEFAULT_MAP_ID,
  ROUND_RESULT_NONE,
  getMap,
  type MatchPhase,
  type PlayerId,
  type TeamId,
  type WeaponId,
} from '@game/shared';

export type GamePhase = 'menu' | 'connecting' | 'playing' | 'disconnected';

/** A roster row in the pre-round lobby. */
export interface LobbyRow {
  id: PlayerId;
  name: string;
  team: TeamId;
  ready: boolean;
  isSelf: boolean;
}

/** One player standing on a pad, as the hub overlay renders them. */
export interface PadOccupant {
  id: PlayerId;
  name: string;
  ready: boolean;
  isSelf: boolean;
}

/** One pad group's queue, resolved for the UI (names, my vote, client clock). */
export interface HubGroupView {
  id: string;
  capacity: number;
  /** True for the single-ring vs-bot pads. */
  solo: boolean;
  /** Bots per human at launch; 0 on player-vs-player pads. */
  botsPerPlayer: number;
  /** Client-clock deadline for the launch countdown; 0 when idle. */
  countdownEndsAtMs: number;
  teams: [PadOccupant[], PadOccupant[]];
  /** Vote tallies aligned with the arena map rotation. */
  voteCounts: number[];
  /** Bot-difficulty tallies aligned with BOT_TIERS. All zero on PvP pads. */
  difficultyCounts: number[];
  /** The local player's vote, or null. Only meaningful when `mine`. */
  myVote: number | null;
  myDifficultyVote: number | null;
  /** True when the local player is standing on this group. */
  mine: boolean;
}

export interface KillFeedEntry {
  id: number;
  attacker: string;
  victim: string;
  headshot: boolean;
  attackerTeam: TeamId;
  victimTeam: TeamId;
}

export interface ScoreboardRow {
  id: PlayerId;
  name: string;
  team: TeamId;
  kills: number;
  deaths: number;
  isSelf: boolean;
}

export interface ChatEntry {
  id: number;
  from: string;
  /** Colours the sender name; null when the sender is already gone. */
  team: TeamId | null;
  self: boolean;
  text: string;
  /** Client clock at arrival — drives the fade-out while the chat is idle. */
  atMs: number;
}

interface GameStore {
  phase: GamePhase;
  statusText: string;

  health: number;
  ammo: number;
  magazine: number;
  weapon: WeaponId;
  weaponName: string;
  reloading: boolean;
  alive: boolean;
  /** Name of the teammate whose eyes the death cam is borrowing, if any. */
  spectatingName: string | null;

  kills: number;
  deaths: number;
  scoreboard: ScoreboardRow[];
  killFeed: KillFeedEntry[];
  chat: ChatEntry[];

  /** Crosshair gap in pixels, derived from the weapon's current bloom. */
  crosshairSpread: number;
  pingMs: number;
  fps: number;
  scoreboardOpen: boolean;
  /** Drives the pause overlay: releasing the cursor mid-round opens the menu. */
  pointerLocked: boolean;
  /**
   * Whether the touch crouch button is latched. Owned by the engine (which can
   * unlatch it when a slide ends) so the on-screen button never disagrees with
   * the bit actually held.
   */
  touchCrouch: boolean;
  /**
   * Whether this player's voice-chat microphone is live. Session state, never
   * persisted — the mic always starts muted. Written by the engine after the
   * (async) permission dance, so the indicator shows what is actually true.
   */
  micOn: boolean;

  // --- match lifecycle -----------------------------------------------------
  matchPhase: MatchPhase;
  /**
   * When the current phase ends, on the *client's* `performance.now` clock. The
   * server sends a duration, not a timestamp, so no clock sync is needed — the
   * lobby ticks this down itself rather than being told the time 30 times a second.
   */
  phaseEndsAtMs: number;
  mapId: string;
  mapName: string;
  mapTagline: string;
  /** Team kill totals for the current game. */
  teamScores: [number, number];
  /** Round wins per team — the score that decides the game. */
  roundWins: [number, number];
  /** The round being played, 1-based. */
  roundNumber: number;
  /** 0/1 = that team took the last round, ROUND_DRAW = draw, ROUND_RESULT_NONE. */
  lastRoundResult: number;
  lobby: LobbyRow[];
  /** The local player's pick; null until they choose in this lobby. */
  loadout: WeaponId | null;

  /** True while standing in the walkable lobby rather than an arena. */
  inHub: boolean;
  hubGroups: HubGroupView[];

  setPhase(phase: GamePhase, statusText?: string): void;
  setScoreboardOpen(open: boolean): void;
  setTouchCrouch(on: boolean): void;
  setMicOn(on: boolean): void;
  pushKill(entry: Omit<KillFeedEntry, 'id'>): void;
  pushChat(entry: Omit<ChatEntry, 'id' | 'atMs'>): void;
  /** Chat is per-room; the engine clears it on every Welcome before the replay. */
  clearChat(): void;
  /** Bulk update from the engine — one call per HUD refresh, not per field. */
  syncHud(patch: Partial<GameStore>): void;
  setMatchState(patch: Partial<GameStore>): void;
  setMap(mapId: string): void;
  reset(): void;
}

const INITIAL = {
  phase: 'menu' as GamePhase,
  statusText: '',
  health: 100,
  ammo: 32,
  magazine: 32,
  weapon: 'smg' as WeaponId,
  weaponName: 'Vector SMG',
  reloading: false,
  alive: true,
  spectatingName: null as string | null,
  kills: 0,
  deaths: 0,
  scoreboard: [] as ScoreboardRow[],
  killFeed: [] as KillFeedEntry[],
  chat: [] as ChatEntry[],
  crosshairSpread: 6,
  pingMs: 0,
  fps: 0,
  scoreboardOpen: false,
  pointerLocked: false,
  touchCrouch: false,
  micOn: false,
  matchPhase: 'waiting' as MatchPhase,
  phaseEndsAtMs: 0,
  mapId: DEFAULT_MAP_ID,
  mapName: getMap(DEFAULT_MAP_ID).name,
  mapTagline: getMap(DEFAULT_MAP_ID).tagline,
  teamScores: [0, 0] as [number, number],
  roundWins: [0, 0] as [number, number],
  roundNumber: 1,
  lastRoundResult: ROUND_RESULT_NONE,
  lobby: [] as LobbyRow[],
  loadout: null as WeaponId | null,
  inHub: false,
  hubGroups: [] as HubGroupView[],
};

let killFeedId = 0;
let chatId = 0;

/**
 * The bridge between the imperative game loop and React. The engine writes here
 * at a fixed low rate (see HUD_SYNC_INTERVAL_MS in engine.ts) — never per frame,
 * so React never re-renders at 200 fps.
 */
export const useGameStore = create<GameStore>((set) => ({
  ...INITIAL,

  setPhase: (phase, statusText = '') => set({ phase, statusText }),

  setScoreboardOpen: (scoreboardOpen) => set({ scoreboardOpen }),

  setTouchCrouch: (touchCrouch) => set({ touchCrouch }),

  setMicOn: (micOn) => set({ micOn }),

  pushKill: (entry) =>
    set((state) => ({
      killFeed: [...state.killFeed, { ...entry, id: ++killFeedId }].slice(-5),
    })),

  pushChat: (entry) =>
    set((state) => ({
      chat: [
        ...state.chat,
        { ...entry, id: ++chatId, atMs: performance.now() },
      ].slice(-50),
    })),

  clearChat: () => set({ chat: [] }),

  syncHud: (patch) => set(patch),

  setMatchState: (patch) => set(patch),

  setMap: (mapId) => {
    const map = getMap(mapId);
    set({
      mapId: map.id,
      mapName: map.name,
      mapTagline: map.tagline,
      inHub: !map.combat,
    });
  },

  reset: () => set({ ...INITIAL }),
}));

/** Non-reactive access for the engine, which must not subscribe to itself. */
export const gameStore = useGameStore;
